import { ReactNode, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Menu, ChevronDown, X, MessageCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import { APP_CONFIG } from '../config';
import { FloatingCallButton } from './FloatingCallButton';
import { StickyQuoteCTA } from './StickyQuoteCTA';

function useWorkingHours() {
  const [isWorkingHours, setIsWorkingHours] = useState(false);

  useEffect(() => {
    const checkHours = () => {
      const now = new Date();
      // Use UK time checking ideally, but system local is ok for client
      const day = now.getDay();
      const hour = now.getHours();
      // Mon-Fri (1-5), 9am (9) to 5pm (16:59)
      if (day >= 1 && day <= 5 && hour >= 9 && hour < 17) {
        setIsWorkingHours(true);
      } else {
        setIsWorkingHours(false);
      }
    };
    
    checkHours();
    const interval = setInterval(checkHours, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  return isWorkingHours;
}

export default function Layout({ children }: { children: ReactNode }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const isWorkingHours = useWorkingHours();
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const helpDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if ((event.target as Element).closest('[data-mobile-menu]')) {
        return;
      }
      
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (activeDropdown === 'appliances') setActiveDropdown(null);
      }
      if (helpDropdownRef.current && !helpDropdownRef.current.contains(event.target as Node)) {
        if (activeDropdown === 'help') setActiveDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    if (!activeDropdown) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [activeDropdown]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const previousFocus = document.activeElement as HTMLElement | null;
    const focusSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const firstFocusable = mobileMenuRef.current?.querySelector<HTMLElement>(focusSelector);
    firstFocusable?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleMenuKeyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        return;
      }

      if (event.key !== 'Tab' || !mobileMenuRef.current) return;

      const focusable = Array.from(
        mobileMenuRef.current.querySelectorAll(focusSelector)
      ) as HTMLElement[];

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleMenuKeyboard);
    return () => {
      document.removeEventListener('keydown', handleMenuKeyboard);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [isMobileMenuOpen]);

  const toggleDropdown = (menu: string) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };

  const menuItems = [
    { name: 'Home', href: '/' },
    { 
      name: 'Appliances', 
      href: '#',
      id: 'appliances',
      dropdown: [
        { name: 'Cooker Hood', href: '/cooker-hood-repairs' },
        { name: 'Dishwasher', href: '/dishwasher-repairs' },
        { name: 'Duel Fuel Cooker', href: '/duel-fuel-cooker-repairs' },
        { name: 'Electric Cooker & Oven', href: '/electric-cooker-oven-repairs' },
        { name: 'Electric Hob', href: '/electric-hob-repairs' },
        { name: 'Fridge Freezer', href: '/fridge-freezer-repairs' },
        { name: 'Gas Cooker & Oven', href: '/gas-cooker-oven-repairs' },
        { name: 'Gas Hob', href: '/gas-hob-repairs' },
        { name: 'Induction Hob', href: '/induction-hob-repairs' },
        { name: 'Microwave', href: '/microwave-repairs' },
        { name: 'Tumble Dryer', href: '/tumble-dryer-repairs' },
        { name: 'Warming Drawer', href: '/warming-drawer-repairs' },
        { name: 'Washing Machine', href: '/washing-machine-repairs' },
        { name: 'Wine Cooler', href: '/wine-cooler-repairs' },
        { name: 'Vacuum Cleaner', href: '/vacuum-cleaner-repairs' },
      ]
    },
    { 
      name: 'Plumbing', 
      href: '#',
      id: 'plumbing',
      dropdown: [
        { name: 'Boiler', href: '/boiler-repairs' },
        { name: 'Radiator', href: '/radiator-repairs' },
        { name: 'Tap', href: '/tap-repairs' },
        { name: 'Toilet', href: '/toilet-repairs' },
        { name: 'Hot Water Heating', href: '/hot-water-heating-repairs' },
        { name: 'Hot Tub', href: '/hot-tub-repairs' },
        { name: 'Air Conditioning', href: '/air-conditioning-repairs' },
        { name: 'Extractor Fan', href: '/extractor-fan-repairs' },
      ]
    },
    { name: 'Customers', href: '/customers' },
    { name: 'Partners', href: '/partners' },
    { name: 'About', href: '/about' },
    { 
      name: 'Help', 
      href: '#',
      id: 'help',
      dropdown: [
        { name: 'Contact Us', href: '/contact-us' },
        { name: 'Privacy & GDPR', href: '/privacy-policy-4' },
        { name: 'Terms & Conditions', href: '/terms-conditions' },
        { name: 'Due Diligence', href: '/due-diligence-checks' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-white focus:text-brand-primary focus:font-bold focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>
      <header className="bg-brand-primary border-b border-brand-alternate sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="flex items-center shrink-0">
              <img src="/fast-repair-app-logo-v2.png" alt="Fast Repair" width="151" height="33" fetchPriority="high" className="h-10 w-auto" />
            </a>
            
            {/* Desktop Navigation */}
            <nav aria-label="Primary navigation" className="hidden lg:flex items-center gap-6 text-slate-200 font-medium ml-4">
              {menuItems.map((item) => (
                <div key={item.name} className="relative" ref={item.id === 'appliances' ? dropdownRef : item.id === 'help' ? helpDropdownRef : undefined}>
                  {item.href.startsWith('http') ? (
                    <a 
                      href={item.href}
                      className="flex items-center gap-1 hover:text-brand-tertiary transition-colors py-2 text-[15px]"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.name}
                    </a>
                  ) : item.dropdown ? (
                    <button
                      type="button"
                      className="flex items-center gap-1 hover:text-brand-tertiary transition-colors py-2 text-[15px]"
                      onClick={() => toggleDropdown(item.id!)}
                      aria-haspopup="menu"
                      aria-expanded={activeDropdown === item.id}
                      aria-controls={`desktop-nav-${item.id}`}
                    >
                      {item.name}
                      <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", activeDropdown === item.id && "rotate-180")} />
                    </button>
                  ) : (
                    <Link 
                      to={item.href}
                      className="flex items-center gap-1 hover:text-brand-tertiary transition-colors py-2 text-[15px]"
                    >
                      {item.name}
                    </Link>
                  )}
                  
                  {item.dropdown && activeDropdown === item.id && (
                    <div
                      id={`desktop-nav-${item.id}`}
                      role="menu"
                      aria-label={`${item.name} submenu`}
                      className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 py-2 animate-in fade-in slide-in-from-top-2 duration-200 z-50"
                    >
                      {item.dropdown.map((subItem) => (
                        <Link 
                          key={subItem.name} 
                          to={subItem.href}
                          role="menuitem"
                          className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-brand-primary transition-colors"
                          onClick={() => setActiveDropdown(null)}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
          
          <div className="flex items-center gap-4 shrink-0">
            <button 
              className="lg:hidden text-slate-200 p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X className="w-7 h-7" aria-hidden="true" /> : <Menu className="w-7 h-7" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div
          id="mobile-menu"
          data-mobile-menu
          ref={mobileMenuRef}
          aria-label="Mobile navigation menu"
          className="lg:hidden fixed inset-0 top-20 z-40 bg-brand-primary border-t border-brand-alternate overflow-y-auto animate-in slide-in-from-top-2 duration-200"
        >
          <nav aria-label="Primary mobile navigation" className="p-4 flex flex-col pt-4">
            {menuItems.map((item) => (
              <div key={item.name} className="border-b border-brand-alternate/50 last:border-0">
                {item.href.startsWith('http') ? (
                  <a 
                    href={item.href}
                    className="flex items-center justify-between py-4 text-lg font-medium text-white"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.name}
                  </a>
                ) : item.dropdown ? (
                  <button
                    type="button"
                    className="w-full flex items-center justify-between py-4 text-lg font-medium text-white text-left"
                    onClick={() => toggleDropdown(item.id!)}
                    aria-haspopup="menu"
                    aria-expanded={activeDropdown === item.id}
                    aria-controls={`mobile-nav-${item.id}`}
                  >
                    {item.name}
                    <ChevronDown className={cn("w-5 h-5 transition-transform duration-200 text-brand-tertiary", activeDropdown === item.id && "rotate-180")} />
                  </button>
                ) : (
                  <Link 
                    to={item.href}
                    className="flex items-center justify-between py-4 text-lg font-medium text-white"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
                
                {item.dropdown && activeDropdown === item.id && (
                  <div id={`mobile-nav-${item.id}`} role="menu" aria-label={`${item.name} submenu`} className="pl-4 pb-4 space-y-3">
                    {item.dropdown.map((subItem) => (
                      <Link 
                        key={subItem.name} 
                        to={subItem.href}
                        role="menuitem"
                        className="block text-slate-300 hover:text-white"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mt-8">
              <Link to="/quote" onClick={() => setIsMobileMenuOpen(false)} className="bg-brand-tertiary text-brand-primary font-bold py-4 rounded-lg flex justify-center w-full shadow-md items-center gap-2 text-lg">
                Get Quote Now
              </Link>
            </div>
          </nav>
        </div>
      )}

      <main id="main-content" className="flex-1 shrink-0 flex flex-col items-center w-full">
        {children}
      </main>
      <footer className="bg-brand-alternate text-slate-400 py-12 pb-32 md:pb-12 mt-auto w-full border-t border-brand-primary">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm space-y-4">
          <div className="flex flex-wrap justify-center gap-4 text-slate-300">
            <Link to="/privacy-policy-4" className="hover:text-white transition-colors">Privacy Policy</Link>
            <span className="text-slate-600">|</span>
            <Link to="/terms-conditions" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
            <span className="text-slate-600">|</span>
            <Link to="/contact-us" className="hover:text-white transition-colors">Contact Us</Link>
          </div>
          <p className="text-slate-400 text-xs leading-relaxed max-w-3xl mx-auto">
            FAST REPAIR is a trading name of CLICKAHEAD MARKETING LTD. Registered Office: Preston Park House, South Road, Brighton, England, BN1 6SB.
            Company Number: 14719973 | VAT Number: 480019216 | ICO Number: ZB605577
          </p>
          <p>&copy; {new Date().getFullYear()} Fast Repair. All rights reserved.</p>
        </div>
      </footer>
      
      {APP_CONFIG.showPhoneNumber && <FloatingCallButton phoneNumber="07941 564 003" />}
      <StickyQuoteCTA />
    </div>
  );
}
